import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { InternationalCommittee } from '../../types/committee';
import { Input } from '../../design-system/components/Input';
import { Text } from '../../design-system/components/Typography';
import { Button } from '../../design-system/components/Button';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { findCommittee, getCommitteeLabel, uniqueInternationalCommittees } from '../../utils/committeeUtils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[5]};
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.colors.icomBlue};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  margin: 0;
  font-family: ${typography.fontFamily.primary};
`;

const SummaryPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  padding: ${spacing[4]} ${spacing[5]};
  background: ${props => props.theme.colors.surface};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
`;

const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
`;

const SummaryLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  min-width: 120px;
  font-family: ${typography.fontFamily.primary};
`;

const Chip = styled.div<{ $variant: 'main' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${borders.radius.full};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props =>
    props.$variant === 'main'
      ? `${props.theme.colors.primary}15`
      : props.theme.colors.surfaceHover};
  color: ${props => props.theme.colors.text};
  border: ${borders.width.base} solid
    ${props =>
      props.$variant === 'main' ? props.theme.colors.primary : props.theme.colors.border};
`;

const ChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: ${borders.radius.full};
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  font-size: ${typography.fontSize.sm};
  line-height: 1;
  padding: 0;
  &:hover {
    background: ${props => props.theme.colors.border};
    color: ${props => props.theme.colors.text};
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  align-items: center;
`;

const FilterToggle = styled.button<{ $active: boolean }>`
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${borders.radius.full};
  border: ${borders.width.base} solid
    ${props => (props.$active ? props.theme.colors.primary : props.theme.colors.border)};
  background: ${props => (props.$active ? `${props.theme.colors.primary}12` : props.theme.colors.surface)};
  color: ${props => (props.$active ? props.theme.colors.primary : props.theme.colors.textSecondary)};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  font-family: ${typography.fontFamily.primary};
`;

const CommitteeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  max-height: 420px;
  overflow-y: auto;
  padding-right: ${spacing[1]};
`;

const CommitteeRow = styled.div<{ $isMain: boolean; $isSecondary: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.base} solid
    ${props =>
      props.$isMain
        ? props.theme.colors.primary
        : props.$isSecondary
          ? props.theme.colors.border
          : props.theme.colors.border};
  background: ${props =>
    props.$isMain
      ? `${props.theme.colors.primary}08`
      : props.$isSecondary
        ? props.theme.colors.surfaceHover
        : props.theme.colors.surface};
`;

const RowHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]};
`;

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CodeBadge = styled.span`
  display: inline-block;
  margin-right: ${spacing[2]};
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borders.radius.sm};
  background: ${props => props.theme.colors.icomBlue};
  color: ${props => props.theme.colors.surface};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  font-family: ${typography.fontFamily.primary};
`;

const CommitteeName = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-family: ${typography.fontFamily.primary};
`;

const CommitteeSubname = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: ${spacing[1]};
  font-family: ${typography.fontFamily.primary};
`;

const RowActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: ${spacing[2]};
`;

const Description = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${typography.lineHeight.relaxed};
  font-family: ${typography.fontFamily.primary};
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  margin-top: ${spacing[1]};
`;

const Counter = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;

interface CommitteeSelectorProps {
  committees: InternationalCommittee[];
  mainCommittee: string;
  secondaryCommittees: string[];
  onMainCommitteeChange: (committeeCode: string) => void;
  onSecondaryCommitteesChange: (committeeCodes: string[]) => void;
  mainTitle?: string;
  mainDescription?: string;
  secondaryTitle?: string;
  secondaryDescription?: string;
}

const CommitteeSelector: React.FC<CommitteeSelectorProps> = ({
  committees,
  mainCommittee,
  secondaryCommittees,
  onMainCommitteeChange,
  onSecondaryCommitteesChange,
  mainTitle,
  mainDescription,
  secondaryTitle,
  secondaryDescription,
}) => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const isGreek = (i18n.language || 'en') === 'gr';
  const uniqueCommittees = useMemo(
    () => uniqueInternationalCommittees(committees, [mainCommittee, ...secondaryCommittees]),
    [committees, mainCommittee, secondaryCommittees]
  );
  const mainCode = findCommittee(uniqueCommittees, mainCommittee)?.code ?? mainCommittee;
  const secondaryCodes = secondaryCommittees
    .map(value => findCommittee(uniqueCommittees, value)?.code ?? value)
    .filter(Boolean);

  const filteredCommittees = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    return uniqueCommittees.filter(committee => {
      const matchesSearch =
        !search ||
        committee.code.toLowerCase().includes(search) ||
        committee.nameEn.toLowerCase().includes(search) ||
        committee.nameGr.toLowerCase().includes(search) ||
        committee.descriptionEn?.toLowerCase().includes(search) ||
        committee.descriptionGr?.toLowerCase().includes(search);

      const isSelected =
        committee.code === mainCode || secondaryCodes.includes(committee.code);

      return matchesSearch && (!showSelectedOnly || isSelected);
    });
  }, [uniqueCommittees, mainCode, secondaryCodes, searchTerm, showSelectedOnly]);

  const handleMainSelect = (committeeCode: string) => {
    if (committeeCode === mainCode) {
      onMainCommitteeChange('');
      return;
    }
    onMainCommitteeChange(committeeCode);
    onSecondaryCommitteesChange(secondaryCodes.filter(code => code !== committeeCode));
  };

  const handleSecondaryToggle = (committeeCode: string) => {
    if (committeeCode === mainCode) return;
    if (secondaryCodes.includes(committeeCode)) {
      onSecondaryCommitteesChange(secondaryCodes.filter(code => code !== committeeCode));
      return;
    }
    if (secondaryCodes.length < 3) {
      onSecondaryCommitteesChange([...secondaryCodes, committeeCode]);
    }
  };

  const mainCommitteeEntity = findCommittee(uniqueCommittees, mainCode);
  const secondaryEntities = secondaryCodes
    .map(code => findCommittee(uniqueCommittees, code))
    .filter((c): c is InternationalCommittee => !!c);

  return (
    <Container>
      <SummaryPanel>
        <SectionTitle style={{ fontSize: typography.fontSize.base }}>
          {t('register.committees.selectedSummary')}
        </SectionTitle>
        <SummaryRow>
          <SummaryLabel>{t('register.committees.main')}</SummaryLabel>
          {mainCommitteeEntity ? (
            <Chip $variant="main">
              {getCommitteeLabel(mainCommitteeEntity, isGreek)}
              <ChipRemove
                type="button"
                aria-label={t('register.committees.removeSelectionNamed', {
                  name: getCommitteeLabel(mainCommitteeEntity, isGreek),
                })}
                onClick={() => onMainCommitteeChange('')}
              >
                ×
              </ChipRemove>
            </Chip>
          ) : (
            <Text size="sm" color="secondary">
              {t('register.committees.noMainSelected')}
            </Text>
          )}
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>{t('register.committees.secondary')}</SummaryLabel>
          {secondaryEntities.length > 0 ? (
            secondaryEntities.map(committee => (
              <Chip key={committee.code} $variant="secondary">
                {getCommitteeLabel(committee, isGreek)}
                <ChipRemove
                  type="button"
                  aria-label={t('register.committees.removeSelectionNamed', {
                    name: committee.code,
                  })}
                  onClick={() => handleSecondaryToggle(committee.code)}
                >
                  ×
                </ChipRemove>
              </Chip>
            ))
          ) : (
            <Text size="sm" color="secondary">
              {t('register.committees.noSecondarySelected')}
            </Text>
          )}
        </SummaryRow>
        {secondaryCodes.length > 0 && (
          <Counter>
            {t('register.committees.secondaryCount', { count: secondaryCodes.length })}
          </Counter>
        )}
      </SummaryPanel>

      <SectionBlock>
        <SectionTitle>{mainTitle ?? t('register.committees.mainCommittee')}</SectionTitle>
        <Text size="sm" color="secondary">
          {mainDescription ?? t('register.committees.mainCommitteeDescription')}
        </Text>
        <Text size="sm" color="secondary">
          {secondaryDescription ?? t('register.committees.secondaryCommitteesDescription')}
        </Text>
        {secondaryTitle && (
          <Text size="sm" weight="semibold" color="text">
            {secondaryTitle}
          </Text>
        )}
        <Toolbar>
          <Input
            type="text"
            placeholder={t('register.committees.searchPlaceholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            iconLeft="🔍"
            fullWidth
            style={{ flex: 1, minWidth: 220 }}
          />
          <FilterToggle
            type="button"
            $active={showSelectedOnly}
            onClick={() => setShowSelectedOnly(prev => !prev)}
          >
            {showSelectedOnly
              ? t('register.committees.showAll')
              : t('register.committees.showSelectedOnly')}
          </FilterToggle>
        </Toolbar>
        <CommitteeList>
          {filteredCommittees.map(committee => {
            const isMain = committee.code === mainCode;
            const isSecondary = secondaryCodes.includes(committee.code);
            const canAddSecondary = secondaryCodes.length < 3 || isSecondary;
            const description = isGreek
              ? committee.descriptionGr || committee.descriptionEn
              : committee.descriptionEn || committee.descriptionGr;

            return (
              <CommitteeRow key={committee.code} $isMain={isMain} $isSecondary={isSecondary}>
                <RowHeader>
                  <RowInfo>
                    <CommitteeName>
                      <CodeBadge>{committee.code}</CodeBadge>
                      {isGreek ? committee.nameGr : committee.nameEn}
                    </CommitteeName>
                    {((isGreek && committee.nameEn) || (!isGreek && committee.nameGr)) && (
                      <CommitteeSubname>
                        {isGreek ? committee.nameEn : committee.nameGr}
                      </CommitteeSubname>
                    )}
                    {description && <Description>{description}</Description>}
                  </RowInfo>
                  <RowActions>
                    <Button
                      type="button"
                      size="sm"
                      variant={isMain ? 'primary' : 'outline'}
                      onClick={() => handleMainSelect(committee.code)}
                      disabled={isSecondary}
                    >
                      {isMain
                        ? t('register.committees.deselectMain')
                        : t('register.committees.setAsMain')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={isSecondary ? 'primary' : 'outline'}
                      onClick={() => handleSecondaryToggle(committee.code)}
                      disabled={isMain || !canAddSecondary}
                    >
                      {isSecondary
                        ? t('register.committees.deselectSecondary')
                        : t('register.committees.addSecondary')}
                    </Button>
                  </RowActions>
                </RowHeader>
              </CommitteeRow>
            );
          })}
        </CommitteeList>
      </SectionBlock>
    </Container>
  );
};

export default CommitteeSelector;
